import { Button } from '@oysterjs/ui/button';
import {
  type MailboxAccountFieldsFragment,
  MailboxAccountStatus,
  OutboundEmailStatus
} from '~/api/graphql';
import { mailClientName } from '~/lib/mailbox-providers';
import { formatSendTime } from '~/lib/scheduled-send';
import { type OutboxRow } from '~/components/mailbox/outbox-rows';

type OutboxScheduleBanner = {
  key: string;
  tone: 'warning' | 'info';
  title: string;
  body: string;
  /** Set on the banners a reconnect fixes. */
  reconnectLabel?: string;
};

type ReconnectableMailbox = Pick<
  MailboxAccountFieldsFragment,
  'id' | 'status' | 'provider' | 'identity'
>;

/**
 * Asks for a mailbox to be reconnected before its `waitingCount` scheduled emails fail. Names
 * the next send time when there is one ahead.
 */
export const mailboxReconnectBanner = (
  account: ReconnectableMailbox,
  waitingCount: number,
  nextSendAt: Date | undefined
): OutboxScheduleBanner => {
  const mailClient = mailClientName(account.provider);
  const emails =
    waitingCount === 1
      ? '1 scheduled email will fail unless you reconnect'
      : `${waitingCount} scheduled emails will fail unless you reconnect`;
  const sendTime = waitingCount === 1 ? 'its send time' : 'their send times';
  return {
    key: `reconnect-${account.id}`,
    tone: 'warning',
    title: `${emails} ${mailClient} before ${sendTime}.`,
    body: `${mailClient} signed Cara out of ${account.identity.email}.${nextSendAt ? ` The next one is set for ${formatSendTime(nextSendAt)}.` : ''}`,
    reconnectLabel: `Reconnect ${mailClient}`
  };
};

/**
 * What the Outgoing page says above the list about scheduled emails: which
 * mailboxes must be reconnected before their scheduled emails can go.
 *
 * Read from the rows on screen, so a long list counts only what is loaded.
 */
export const outboxScheduleBanners = (
  rows: OutboxRow[],
  accounts: ReconnectableMailbox[],
  nowMs: number
): OutboxScheduleBanner[] => {
  const scheduledSends = rows.flatMap((row) =>
    row.outbound?.status === OutboundEmailStatus.Scheduled ? [row.outbound] : []
  );
  const banners: OutboxScheduleBanner[] = [];

  for (const account of accounts) {
    if (account.status !== MailboxAccountStatus.NeedsReconnect) continue;
    const waiting = scheduledSends.filter((send) => send.mailboxAccountId === account.id);
    if (waiting.length === 0) continue;
    const nextSendAt = waiting
      .map((send) => (send.scheduledFor ? new Date(send.scheduledFor) : null))
      .filter((sendAt): sendAt is Date => !!sendAt && sendAt.getTime() > nowMs)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    banners.push(mailboxReconnectBanner(account, waiting.length, nextSendAt));
  }

  return banners;
};

const TONE_CLASS: Record<OutboxScheduleBanner['tone'], string> = {
  warning:
    'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200',
  info: 'border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 text-sky-900 dark:text-sky-200'
};

export const OutboxScheduleBanners = (props: { banners: OutboxScheduleBanner[] }) => {
  if (props.banners.length === 0) {
    return null;
  }
  return (
    <div className="mb-4 space-y-2">
      {props.banners.map((banner) => (
        <div
          key={banner.key}
          role="status"
          className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border px-4 py-3 text-sm ${TONE_CLASS[banner.tone]}`}
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium">{banner.title}</p>
            <p className="mt-0.5">{banner.body}</p>
          </div>
          {banner.reconnectLabel && (
            <Button color="white" href="/app/settings/mailbox">
              {banner.reconnectLabel}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};
